import {
  AdditionalConfiguration,
  NumericFormatType,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControl,
  ReportSectionConfigurationControlType,
  ReportSectionDataSection,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { TopShareholderModel } from './top-share-holders.model';

export class TopShareholdersConfiguration extends ReportSectionConfigurationBase {
  typeName = 'TopShareholdersConfiguration';
  nodataLocalizationKeyName = 'topShareHolders';
  readonly reportSection = ReportSection.TopShareHolders;
  readonly applicableReportTypes = [ReportType.OwnershipAnalyticsReport];
  readonly reportViewModelSections = [ReportViewModelSection.OwnershipAnalyticsReport_TopShareholders];
  minSize = 4;
  maxSize = 4;
  defaultSize = 4;

  readonly availableDataSections: ReportSectionDataSection[] = [
    {
      name: 'topShareHoldersRank',
      displayName: 'Rank',
      isSelected: true,
      canConfigure: false,
      unit: 1,
      property: 'rank',
      isStatic: true,
      type: ReportSectionConfigurationControlType.Number
    },
    {
      name: 'topShareHoldersInstitutionName',
      displayName: 'Institution',
      isSelected: true,
      canConfigure: false,
      unit: 2,
      property: 'institutionName',
      isStatic: true,
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'topShareHoldersEquityAssetsMm',
      displayName: 'Equity Assets',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'equityAssetsMm',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'topShareHoldersShares',
      displayName: 'Shares',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'shares',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'topShareHoldersShareChange',
      displayName: 'Share Change',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'shareChange',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'topShareHoldersOrientation',
      displayName: 'Orientation',
      isSelected: true,
      canConfigure: true,
      unit: 1,
      property: 'orientation',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'topShareHoldersStyle',
      displayName: 'Style',
      isSelected: true,
      canConfigure: true,
      unit: 1,
      property: 'style',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'topShareHoldersTurnover',
      displayName: 'Turnover',
      isSelected: true,
      canConfigure: true,
      unit: 1,
      property: 'turnover',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'topShareHoldersCountry',
      displayName: 'Country',
      isSelected: true,
      canConfigure: true,
      unit: 1,
      property: 'country',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'topShareHoldersPctSO',
      displayName: '% S/O',
      isSelected: true,
      canConfigure: true,
      unit: 1,
      property: 'pctSO',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.Percentage
    },
    {
      name: 'topShareHoldersCostBasisUsd',
      displayName: 'Cost Basis, USD',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'costBasisUsd',
      type: ReportSectionConfigurationControlType.Decimal
    }
  ];

  override additionalConfiguration: AdditionalConfiguration = {
    listCountConfig: {
      min: 1,
      max: 100,
      default: 50,
      value: 50,
      listCountHeader: 'noOfTopShareholders'
    },
    activeInvestorsOnly: false
  };

  override configurationControls: ReportSectionConfigurationControl[] = [
    {
      name: 'topShareholdersCount',
      displayName: 'Number of top shareholders',
      type: 'listCount',
      defaultOption: 50
    },
    {
      name: 'activeInvestorsOnly',
      displayName: 'Active Investors only',
      type: 'activeInvestorsOnly',
      defaultOption: false
    }
  ];

  get name(): string {
    if (this.appliedReportType === ReportType.OwnershipAnalyticsReport) {
      return 'topShareHolders';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.OwnershipAnalyticsReport) {
      return 'Top Shareholders';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface TopShareholdersData {
  [ReportViewModelSection.OwnershipAnalyticsReport_TopShareholders]: {
    topShareholders: TopShareholderModel[];
  };
}
